/**
 * TODO: deprecate this in favor of DropdownField
 */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Dropdown as SemanticDropdown, Label } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import commonPropTypes from '../../../constants/commonPropTypes'
import { formFieldWrapper } from '../formFieldWrapper'
import { getThemedCompact } from '../util'
import { useApplicationTheme } from '../../applicationTheme'
import Chip from '../../elementals/chip'
import InfoPill from '../../elementals/infoPill'
import { ColorNames } from '../../../constants/colorVars'
import styles from '../styles.scss'
const cx = classnames.bind(styles)

/**
 * Render's labels in a manner that shows e.g. [First One] 4 More... - if there were 5 selected.  The label for the
 * second item is highjacked to just show "4 More..." for example.  The third on are hidden by sending null.
 *
 * @param {bool} isOpen -- true if dropdown is open
 * @param {number} remaining -- count of remaining items selected
 * @param {function} renderLabel -- custom label rendering function
 * @param {object} item -- item to render
 * @param {number} index -- index of item to render
 * @param {object} defaultProps -- sent by semantic dropdown
 * @returns special rendering of label
 */
const renderLabelCondensed = (
  isOpen,
  remaining,
  renderLabel,
  item,
  index,
  defaultProps
) => {
  if (index === 0 || isOpen) {
    return renderLabel
      ? renderLabel(item, index, defaultProps)
      : SemanticDropdown.defaultProps.renderLabel(item, index, defaultProps)
  }

  if (index === 1) {
    return <Label className={cx('more-label')}>{remaining} More...</Label>
  }

  return null
}

const chipRenderLabel = (elmId, chipColor) => (item, index, defaultProps) =>
  (
    <Chip
      elmId={`${elmId}-chip-${index}`}
      type='input'
      size='small'
      color={chipColor}
      selectionStyle='light'
      content={item.text}
      onClick={() => defaultProps.onRemove(null, defaultProps)}
    />
  )

const Select = ({
  elmId,
  placeholder,
  fluid = true,
  label,
  renderLabel,
  useChipLabels,
  condensedWhenClosed,
  onOpen = _.noop,
  onClose = _.noop,
  value,
  showError,
  multiple,
  showMultiSelectOptionsBelow,
  compact,
  disabled,
  options = [],
  chipColor,
  className,
  ...rest
}) => {
  const [isOpen, setOpen] = useState(false)

  // Resolve the themed compact default and honor it on the control, like InputField.
  const theme = useApplicationTheme()
  const newCompact = getThemedCompact(theme, compact)

  const onOpenEx = (event, data) => {
    setOpen(true)
    onOpen(event, data)
  }

  const onCloseEx = (event, data) => {
    setOpen(false)
    onClose(event, data)
  }

  const remaining = value?.length > 1 ? value.length - 1 : null

  // If value is undefined and multiple set to true
  // it gives a warning 'Dropdown value must be an array when multiple is set.
  // Received type: Undefined
  if (typeof value === 'undefined' && multiple) {
    value = []
  }

  const hasValues = _.isArray(value) && value.length > 0
  const effectivePlaceholder =
    useChipLabels && hasValues ? '' : placeholder || label

  const effectiveChipColor = chipColor || undefined

  const effectiveRenderLabel = showMultiSelectOptionsBelow
    ? () => ({ content: '', style: { display: 'none' } })
    : useChipLabels
    ? chipRenderLabel(elmId, effectiveChipColor)
    : renderLabel

  const handleRemoveChip = val => {
    if (rest.onChange) {
      const newValue = (value || []).filter(v => v !== val)
      rest.onChange(null, { value: newValue })
    }
  }

  return (
    <div className={cx('dropdown')}>
      <SemanticDropdown
        className={`${cx('dropdown', {
          condensed: !isOpen && condensedWhenClosed && remaining,
        })}${newCompact ? ' compact' : ''}${className ? ` ${className}` : ''}`}
        selection
        data-elm-id={`${elmId}_select`}
        placeholder={effectivePlaceholder}
        fluid={fluid}
        disabled={disabled}
        options={options}
        renderLabel={
          condensedWhenClosed
            ? renderLabelCondensed.bind(
                null,
                isOpen,
                remaining,
                effectiveRenderLabel
              )
            : effectiveRenderLabel
        }
        onOpen={onOpenEx}
        onClose={onCloseEx}
        error={showError}
        multiple={multiple}
        value={value}
        {...rest}
      />
      {showMultiSelectOptionsBelow &&
        multiple &&
        _.isArray(value) &&
        value.length > 0 && (
          <div
            className={cx('multi-select-below')}
            data-elm-id={`${elmId}_selected_chips`}
          >
            {value.map((val, index) => {
              const option = _.find(options, { value: val })
              return (
                <Chip
                  key={val}
                  elmId={`${elmId}-chip-below-${index}`}
                  type='input'
                  size={newCompact ? 'medium' : 'large'}
                  selectionStyle='light'
                  color={effectiveChipColor}
                  disabled={!!disabled}
                  content={option ? option.text : val}
                  onClick={() => handleRemoveChip(val)}
                />
              )
            })}
          </div>
        )}
    </div>
  )
}

Select.propTypes = {
  ...commonPropTypes,
  options: PropTypes.array,
  condensedWhenClosed: PropTypes.bool,
  useChipLabels: PropTypes.bool,
  showMultiSelectOptionsBelow: PropTypes.bool,
  chipColor: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
}

const SelectFieldInner = formFieldWrapper((fieldProps, showError) => (
  <Select {...fieldProps} showError={showError} />
))

const chipViewModeRenderer = options => value => {
  if (!value || !_.isArray(value) || value.length === 0) return null
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '3px',
      }}
    >
      {value.map(val => {
        const option = _.find(options, { value: val })
        return (
          <InfoPill
            key={val}
            content={option ? option.text : val}
            size='small'
            backgroundColor={ColorNames.InfoExtraLight}
            foregroundColor={ColorNames.Info}
          />
        )
      })}
    </div>
  )
}

const SelectField = props => {
  const {
    useChipLabels,
    options,
    viewModeRenderer,
    showMultiSelectOptionsBelow,
    chipColor,
    ...rest
  } = props
  const effectiveViewModeRenderer =
    useChipLabels && !viewModeRenderer
      ? chipViewModeRenderer(options)
      : viewModeRenderer
  return (
    <SelectFieldInner
      {...rest}
      options={options}
      useChipLabels={useChipLabels}
      showMultiSelectOptionsBelow={showMultiSelectOptionsBelow}
      chipColor={chipColor}
      viewModeRenderer={effectiveViewModeRenderer}
    />
  )
}

export default SelectField
